import { useState, useRef, useEffect } from 'react';
import { Dialog } from '@material-ui/core';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { trI18n } from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import ButtonOption from '@/component/common/ButtonOption';
import FlagMessageCopyLink from '@/component/common/FlagMessageCopyLink';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { signOut } from '@/service/authenService';
import { hasKnowledgeChatPermission } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import AttachFileThumbnail from '../components/AttachFileThumbnail';
import {
    normalizeChatPostFile,
    findMenuFile,
    getThumbImageLinks,
    downloadAttachFile,
    buildShareLinkUrl,
    focusToMessageOnThreadChat,
} from './attachThumbnailHandler';
import { useSetRecoilState } from 'recoil';
import { setPanelSelector } from '@/state/selector/panelSelector';
import useTranslatedAttachmentDisplay, { ATTACHMENT_DISPLAY_CONTEXT } from '@/hooks/useTranslatedAttachmentDisplay';
import { toTranslatedDisplayFile } from '@/common/utils/translatedAttachmentUtils';

/**
 * Shared per-file data/option/preview/download/logout logic for the attachment thumbnails,
 * extracted from AttachThumbnailList so both the list and the "view all" modal reuse it with
 * zero duplication.
 *
 * The hook owns the shared state (copy-link flash, image preview, logout popup). It exposes a
 * ready-rendered `dialogs` node (preview <Dialog> + logout <ConfirmDialog>) rather than raw
 * state+setters: this keeps BOTH callers to a single `{dialogs}` render with no duplicated
 * dialog JSX and no leaking of the internal setters.
 *
 * `thumbnailSize` is the resolved size preset passed in by the caller (the list/modal call
 * useAttachThumbnailSize once and pass the result down), used as the `size` of each thumbnail.
 *
 * `ownAttachments` / `chatPostFiles` are returned already sorted by createdAt (old->new) so a
 * caller can render them directly; the list still slices via buildThumbnailFiles.
 */
const byCreatedAt = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

const useAttachThumbnailData = ({ ticketDetail, panelType, viewKey, isListContext = false, options, attachBadge, thumbnailSize, projectInuseDetail, onCloseModal }) => {
    // hooks
    useTranslationSubscription();

    // state
    const [copiedFileUrl, setCopiedFileUrl] = useState(null);
    const [openPreviewImage, setOpenPreviewImage] = useState(null);
    const [isShowPopupLogout, setIsShowPopupLogout] = useState(false);
    const [errorModal, setErrorModal] = useState({isOpen: false, msg: ''});
    const addNewPanel = useSetRecoilState(setPanelSelector);

    // Knowledge list has slightly different data rules than the ticket list.
    const isKnowledge = panelType === Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST ||
        [Constant.TICKET_TYPE.KNOWLEDGE, Constant.TICKET_TYPE.MASTER_KNOWLEDGE].includes(Number(ticketDetail?.ticketType))
    const isDraft = ticketDetail?.public === Constant.TICKET_PUBLIC.DRAFT;

    // A page-knowledge attach file belongs to the ROOT ticket, so its share link must use the
    // root ticket's dataShareLink (carried on the file record), NOT the page ticket's own
    // (which is empty/invalid -> would build a wrong URL). Resolve the correct source per file.
    const isPageKnowledge = Number(ticketDetail?.ticketType) === Constant.TICKET_TYPE.PDF_PAGE_KNOWLEDGE;
    const resolveShareLinkData = file =>
        isPageKnowledge ? Utils.convertJSONDataToObject(file?.dataShareLink) : dataShareLink;

    // "Go to message" needs chat permission: knowledge is gated by hasKnowledgeChatPermission,
    // other tickets always allow it.
    const canGoToMessage = isKnowledge
        ? hasKnowledgeChatPermission(ticketDetail, projectInuseDetail || {})
        : true;

    // `attachFiles` may arrive as an array (ticket detail) or a JSON string (list/modal).
    // Normalize to an array so .filter is always safe.
    const attachFilesRaw = Utils.convertJSONDataToObject(ticketDetail?.attachFiles);
    const attachFiles = Array.isArray(attachFilesRaw) ? attachFilesRaw : [];
    const dataShareLink = Utils.convertJSONDataToObject(ticketDetail?.dataShareLink);

    // Own attachments:
    // - knowledge: ticketKnowledge.attachments
    // - ticket: ticketDetail.attachment, hidden when isHideAttachment is set
    const ownAttachmentsRaw = isKnowledge
        ? ticketDetail?.attachments || []
        : ticketDetail?.isHideAttachment
        ? []
        : ticketDetail?.attachment || [];

    // Chat-post attach files; knowledge also hides them when chat is not permitted.
    const chatPostFilesRaw = attachFiles.filter(i => {
        if (Utils.getPrefixOfId(i?.originId) !== Constant.ID_PREFIX.CHAT_POST) return false;
        if (isKnowledge) {
            return ticketDetail?.chatPermission !== Constant.KNOWLEDGE_CHAT_PERMISSION.NO;
        }
        return true;
    });

    const ownAttachments = [...ownAttachmentsRaw].sort(byCreatedAt);
    const chatPostFiles = [...chatPostFilesRaw].sort(byCreatedAt);

    // CR-303134: an own attachment shows its translation in the viewer's main language when the
    // 「翻訳版」toggle is ON - the list toggle in a list / its modal, the detail toggle elsewhere.
    const { getTranslation } = useTranslatedAttachmentDisplay({
        attachFiles,
        context: isListContext ? ATTACHMENT_DISPLAY_CONTEXT.LIST : ATTACHMENT_DISPLAY_CONTEXT.DETAIL,
        panelType,
        viewKey,
        contentId: ticketDetail?.id
    });

    // Keep the copy-flash timeout so we can clear it on unmount (avoids setState-after-unmount).
    const copyFlashTimeoutRef = useRef(null);
    useEffect(() => () => clearTimeout(copyFlashTimeoutRef.current), []);

    const copyShareLink = (attachFile, keyUrl) => {
        const linkUrl = buildShareLinkUrl(attachFile, resolveShareLinkData(attachFile));
        navigator.clipboard.writeText(linkUrl).catch(err => console.log('copyShareLink error', err));
        setCopiedFileUrl(keyUrl);
        clearTimeout(copyFlashTimeoutRef.current);
        copyFlashTimeoutRef.current = setTimeout(() => setCopiedFileUrl(null), 3000);
    };

    const buildOptions = (file, menuFile, isChatPost, isTranslatedDisplay = false) => {
        if (isDraft) return [];

        const getUrlOption = {
            key: 'get-url',
            visible: Boolean(menuFile?.shortKey) && !Utils.isEmptyObject(resolveShareLinkData(menuFile)),
            closeOnClick: false,
            content: (
                <FlagMessageCopyLink
                    title={trI18n('KNOWDEMSG002')}
                    placement="top-start"
                    arrow={true}
                    open={copiedFileUrl === file?.fileUrl}
                >
                    <ButtonOption onClick={() => copyShareLink(menuFile, file?.fileUrl)}>
                        <span className="attach-file-thumbnail-option-label">
                            {trI18n('GET_URL')}
                        </span>
                    </ButtonOption>
                </FlagMessageCopyLink>
            )
        };

        // "Go to this attachment's message": only for chat-post files (not field attachments).
        const goToMessageOption = {
            key: 'go-to-message',
            visible: Boolean(isChatPost) && canGoToMessage,
            content: (
                <ButtonOption onClick={() => focusToMessageOnThreadChat({file, ticketDetail, addNewPanel, setErrorModal, onCloseModal})}>
                    <span className="attach-file-thumbnail-option-label">
                        {trI18n('GO_TO_ATTACHMENT_MESSAGE')}
                    </span>
                </ButtonOption>
            )
        };

        // While a translation is shown only「URLを取得」remains (CR-303134 BR-5); it copies the
        // translation's own share link because menuFile is the translation's record.
        if (isTranslatedDisplay) return [getUrlOption];

        // Caller-provided extra options (e.g. knowledge "split PDF"), per file.
        const extra = typeof options === 'function' ? options(file, menuFile) || [] : [];

        return [getUrlOption, ...extra, goToMessageOption];
    };

    const renderThumbnail = (rawFile, key) => {
        const isChatPost = Utils.getPrefixOfId(rawFile?.originId) === Constant.ID_PREFIX.CHAT_POST;
        const originalFile = normalizeChatPostFile(rawFile);
        // Chat-post files are never translated from the attachment menu.
        const translation = isChatPost ? null : getTranslation(originalFile);
        const file = translation ? toTranslatedDisplayFile(originalFile, translation) : originalFile;
        const isImage = Utils.isImageFile(file.fileName);
        const { imgThumbnailLink, imgLink } = getThumbImageLinks(file, ticketDetail, attachFiles);
        // Merge the menu-metadata record (shortKey, fileInfo, idTicketFileKnowledge, splitPdf)
        // with the rendered file so the file's own fields win, matching the old
        // `openFileOption = {...attachmentFile, ...file}`. Callers' `options` and the share-link
        // copy both rely on this merged object. A shown translation uses its own record.
        const menuFile = translation ? { ...file, ...translation } : { ...findMenuFile(attachFiles, file?.fileUrl), ...file };

        const onClickFile = () => {
            if (isImage) {
                setOpenPreviewImage(imgLink);
            } else {
                downloadAttachFile(file, ticketDetail, false, setIsShowPopupLogout);
            }
        };

        const badge = typeof attachBadge === 'function' ? attachBadge(file) : null;

        return (
            <AttachFileThumbnail
                key={key}
                fileName={file.fileName}
                fileSize={file.fileSize}
                fileKey={file.fileUrl}
                imageUrl={isImage ? imgThumbnailLink : undefined}
                size={thumbnailSize}
                onClickFile={onClickFile}
                options={buildOptions(file, menuFile, isChatPost, Boolean(translation))}
                badgeText={badge?.badgeText}
                badgeColor={badge?.badgeColor}
            />
        );
    };

    const dialogs = (
        <>
            <Dialog
                open={!!openPreviewImage}
                onClose={() => setOpenPreviewImage(null)}
                maxWidth="md"
            >
                <img
                    alt=""
                    src={openPreviewImage}
                    style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
                />
            </Dialog>
            <ConfirmDialog
                isOpen={isShowPopupLogout}
                msgConfirm={
                    <div>
                        <span>{trI18n('MSG-CM-EXPIRED-SESSION-1')}</span>
                        <br />
                        <span>{trI18n('MSG-CM-EXPIRED-SESSION-2')}</span>
                    </div>
                }
                textSubmit={trI18n('CM-BUTTON-OK')}
                okCallBack={() => signOut()}
                cancelCallBack={() => setIsShowPopupLogout(false)}
                cancelClassName={'btn-confirm-cancel'}
            />
            <ConfirmDialog
                isOpen={errorModal?.isOpen}
                msgConfirm={errorModal?.msg}
                textSubmit={trI18n('CM-BUTTON-OK')}
                okCallBack={() => {
                    setErrorModal({isOpen: false, msg: ''})
                    window.location.reload();
                }}
            />
        </>
    );

    return { thumbnailSize, ownAttachments, chatPostFiles, renderThumbnail, dialogs };
};

export default useAttachThumbnailData;
